import {useEffect, useMemo, useRef} from 'react';
import {Box, Typography} from '@mui/material';
import {FabricObject} from 'fabric';
import {useTranslation} from 'react-i18next';
import {useFloorplanContext} from '../hooks/useFloorplanContext';
import {
    buildSelectionOverlay,
    buildTableGroupOverlays,
    getTableGroupPadding,
    getTableGroupMemberId,
    StoredTableGroup,
    TableGroupKind,
    tableIdSetMatches,
    TableOverlayLike,
} from '../utils/table-groups';

interface Props {
    groups: StoredTableGroup[];
    selectedTableIds: string[];
    onToggleTableSelection: (tableId: string) => void;
    interactive: boolean;
}

// Keep one visual style per stored group kind so the overlay and label stay consistent.
const GROUP_STYLES: Record<TableGroupKind, {
    border: string;
    background: string;
    labelBg: string;
    labelColor: string
}> = {
    combination: {
        border: 'rgba(25, 118, 210, 0.4)',
        background: 'transparent',
        labelBg: 'rgba(250, 250, 250, 0.96)',
        labelColor: 'rgba(25, 118, 210, 0.4)',
    },
    notBookable: {
        border: 'rgba(211, 47, 47, 0.4)',
        background: 'transparent',
        labelBg: 'rgba(250, 250, 250, 0.96)',
        labelColor: 'rgba(211, 47, 47, 0.4)',
    },
};

const SELECTION_STYLES = {
    border: 'rgba(20, 20, 20, 0.88)',
    borderHover: 'rgba(20, 20, 20, 0.55)',
    background: 'rgba(20, 20, 20, 0.08)',
    backgroundHover: 'rgba(20, 20, 20, 0.04)',
    outline: 'rgba(20, 20, 20, 0.7)',
    outlineBackground: 'rgba(20, 20, 20, 0.05)',
};

// Measure the current table objects once so overlay geometry can be derived in canvas space.
const buildTableTargets = (objects: FabricObject[]): TableOverlayLike[] => {
    return objects
        .map((obj) => {
            const memberId = getTableGroupMemberId(obj.floorPlanItem);
            if (!memberId) return null;

            const bounds = obj.getBoundingRect();
            return {
                id: memberId,
                x: bounds.left,
                y: bounds.top,
                width: bounds.width,
                height: bounds.height,
            };
        })
        .filter((target): target is TableOverlayLike => target !== null);
};

const EditTableGroupsOverlay: React.FC<Props> = ({
                                                     groups,
                                                     selectedTableIds,
                                                     onToggleTableSelection,
                                                     interactive,
                                                 }) => {
    const {t} = useTranslation();
    const {canvas, showSeatsAroundTable} = useFloorplanContext();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const groupPadding = getTableGroupPadding(showSeatsAroundTable);

    // Rebuild the clickable table hit areas from the current canvas objects.
    const tableTargets = useMemo(() => {
        if (!canvas) return [];
        return buildTableTargets(
            canvas.getObjects().filter((obj) => !!obj.floorPlanItem?.table),
        );
    }, [canvas, groups, selectedTableIds]);

    // Stored groups render as overlay boxes around the tables they contain.
    const groupOverlays = useMemo(
        () => buildTableGroupOverlays(groups, tableTargets, groupPadding),
        [groupPadding, groups, tableTargets],
    );

    // When the current selection exactly matches a stored group, that group becomes the active highlight.
    const activeGroupOverlay = useMemo(
        () => groupOverlays.find((group) => tableIdSetMatches(group.tableIds, selectedTableIds)) ?? null,
        [groupOverlays, selectedTableIds],
    );

    // Ad-hoc selections still get a preview box before they are saved as a real group.
    const adHocSelectionOverlay = useMemo(() => {
        if (!interactive) return null;
        // Once the selection already matches a stored group, the stored overlay becomes the source of truth.
        if (activeGroupOverlay) return null;
        return buildSelectionOverlay(
            selectedTableIds,
            tableTargets,
            'Selected',
            groupPadding,
        );
    }, [activeGroupOverlay, groupPadding, interactive, selectedTableIds, tableTargets]);

    useEffect(() => {
        if (!canvas) return;

        const syncTransform = () => {
            const wrapper = wrapperRef.current;
            const viewportTransform = canvas.viewportTransform;
            if (!wrapper || !viewportTransform) return;
            wrapper.style.transform = `matrix(${viewportTransform[0]}, 0, 0, ${viewportTransform[3]}, ${viewportTransform[4]}, ${viewportTransform[5]})`;
        };

        syncTransform();

        // Follow canvas pan and zoom by mirroring Fabric's current viewport transform onto the HTML overlay layer.
        canvas.on('after:render', syncTransform);
        return () => {
            canvas.off('after:render', syncTransform);
        };
    }, [canvas]);

    return (
        <div
            ref={wrapperRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                transformOrigin: '0 0',
                zIndex: 4,
                pointerEvents: 'none',
            }}
        >
            {/* These expanded hit areas make table picking easier while group mode is active. */}
            {interactive && tableTargets.map((table) => {
                const isSelected = selectedTableIds.includes(table.id);
                return (
                    <Box
                        key={table.id}
                        onClick={() => onToggleTableSelection(table.id)}
                        sx={{
                            position: 'absolute',
                            left: table.x - 10,
                            top: table.y - 10,
                            width: table.width + 20,
                            height: table.height + 20,
                            borderRadius: 2,
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                            zIndex: isSelected ? 3 : 1,
                            border: isSelected ? `3px solid ${SELECTION_STYLES.border}` : '2px solid transparent',
                            background: isSelected ? SELECTION_STYLES.background : 'transparent',
                            transition: 'background-color 0.15s ease, border-color 0.15s ease',
                            '&:hover': {
                                borderColor: isSelected ? SELECTION_STYLES.border : SELECTION_STYLES.borderHover,
                                background: isSelected ? SELECTION_STYLES.background : SELECTION_STYLES.backgroundHover,
                            },
                        }}
                    />
                );
            })}

            {/* Stored groups always render underneath the click targets so selection remains interactive. */}
            {groupOverlays.map((group) => {
                const style = GROUP_STYLES[group.kind];
                const isActive = activeGroupOverlay?.id === group.id;
                return (
                    <Box
                        key={group.id}
                        sx={{
                            position: 'absolute',
                            left: group.x,
                            top: group.y,
                            width: group.width,
                            height: group.height,
                            border: `2px dashed ${style.border}`,
                            background: style.background,
                            borderRadius: 2,
                            boxSizing: 'border-box',
                            boxShadow: isActive ? `0 0 0 2px ${style.border} inset` : 'none',
                            zIndex: 2,
                            pointerEvents: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -2,
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                px: 0.75,
                                py: 0.2,
                                borderRadius: 1,
                                bgcolor: style.labelBg,
                                border: `1px solid ${style.border}`,
                                zIndex: 3,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Typography variant="caption" sx={{fontWeight: 600, color: style.labelColor}}>
                                {t(group.label, {defaultValue: group.label})}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}

            {adHocSelectionOverlay && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: adHocSelectionOverlay.x,
                        top: adHocSelectionOverlay.y,
                        width: adHocSelectionOverlay.width,
                        height: adHocSelectionOverlay.height,
                        border: `3px dashed ${SELECTION_STYLES.outline}`,
                        background: SELECTION_STYLES.outlineBackground,
                        borderRadius: 2,
                        boxSizing: 'border-box',
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}
                />
            )}
        </div>
    );
};

export default EditTableGroupsOverlay;
